import { useShallow } from 'zustand/react/shallow'

import { StoreState, useStore } from '~/store/store'

import { createApplyFilterProfile, ToggleFilteredBySelectionFn } from './filterProfileUtils'
import { type AnyFilter, getPageTypeByPathname, pageFilters } from './pageFilters'

type FilterActions = {
    toggleFilteredBy: (args: { value: string; label: string; type: string }) => void
    toggleFilteredBySelection: ToggleFilteredBySelectionFn
    removeAllFilterByType: (type: string) => void
    removeSingleFilter?: (id: string) => void
    emptyFilters: () => void
    applyFilterProfile: ReturnType<typeof createApplyFilterProfile>
}

export type FilterHookResult = FilterActions & {
    selectAllFilters: (state: StoreState) => AnyFilter[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

export const createFilterSliceSelectorByPathname = (pathname: string) => {
    const pageType = getPageTypeByPathname(pathname)
    const { sliceKey } = pageFilters[pageType]
    return (state: StoreState) => state[sliceKey]
}

export const createFilterSliceActionsSelectorByPathname = (pathname: string) => (state: StoreState) => {
    const actions = createFilterSliceSelectorByPathname(pathname)(state).actions
    return {
        toggleFilteredBy: actions.toggleFilteredBy as (args: { value: string; label: string; type: string }) => void,
        toggleFilteredBySelection: actions.toggleFilteredBySelection as ToggleFilteredBySelectionFn,
        removeAllFilterByType: actions.removeAllFilterByType as (type: string) => void,
        removeSingleFilter: actions.removeSingleFilter as (id: string) => void,
        emptyFilters: actions.emptyFilters,
    }
}

export function useFiltersByPathname(pathname: string): FilterHookResult {
    const pageType = getPageTypeByPathname(pathname)
    const { selectors } = pageFilters[pageType]

    const actions = useStore(useShallow(createFilterSliceActionsSelectorByPathname(pathname)))

    const toggleFilteredBySelection = actions.toggleFilteredBySelection as ToggleFilteredBySelectionFn
    const applyFilterProfile = createApplyFilterProfile(toggleFilteredBySelection)

    return {
        // Actions
        ...actions,
        applyFilterProfile,
        // Selectors
        ...selectors,
    }
}
